// S3 文滑动+图固定
import { FC, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import clsx from 'clsx'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import PdMedia from '@components/pd-media/pd-media'
import { IMutipleMedia } from '@/common/pd-meta-components-types'

gsap.registerPlugin(useGSAP, ScrollTrigger)

const ScrollSwitchMedia: FC<IMutipleMedia> = ({ theme, title, mediaGroup = [] }) => {
  const isMobileDevice = useMobileDetector()
  const elementsRef = useRef<Map<number, Element>>(new Map())
  const [windowInnerHeight, setWindowInnerHeight] = useState<number>(900)
  const [activeId, setActiveId] = useState<string>(mediaGroup[0]?.title || '')
  const containerRef = useRef<HTMLDivElement>(null)

  useGSAP(() => {
    ScrollTrigger.refresh()
    const items = isMobileDevice ? [] : elementsRef.current
    items.forEach((item: Element) => {
      gsap.fromTo(
        item,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          scrollTrigger: {
            scroller: '#main',
            trigger: item,
            start: 'top bottom',
            end: 'center center',
            scrub: 1,
            onEnter: (...arg: any) => {
              const id = arg?.[0].trigger?.dataset.id
              setActiveId(id)
            },
          },
        }
      )

      gsap.to(item, {
        scrollTrigger: {
          scroller: '#main',
          trigger: item,
          start: 'bottom top',
          end: 'center center',
          scrub: 1,
          onEnterBack: (...arg: any) => {
            const id = arg?.[0].trigger?.dataset.id

            setActiveId(id)
          },
        },
      })
    })
  }, [mediaGroup, isMobileDevice])

  useEffect(() => {
    setWindowInnerHeight(!!window ? window.innerHeight : 900)
  }, [])

  return (
    <section
      className={clsx(
        'w-full h-fit pt-15 pb-20 px-0',
        'laptop:py-40 laptop:px-0',
        `pd-theme-${theme} bg-container-primary`
      )}
      ref={containerRef}
    >
      <div className={clsx('w-full mx-auto', 'laptop:w-300')}>
        <div
          className={clsx(
            'font-semibold',
            'text-start mb-5 pt-5 pb-10 px-6',
            'laptop:text-center laptop:py-0 laptop:mb-30 laptop:px-0',
            'text-fc-primary pd-mega-title'
          )}
        >
          {title}
        </div>

        <div
          className={clsx(
            'flex overflow-visible',
            'flex-col-reverse',
            'laptop:flex-row laptop:justify-between laptop:items-start laptop:gap-12'
          )}
        >
          <div
            className={clsx('flex flex-col', 'gap-15 w-full', 'laptop:pt-40 laptop:pb-50 laptop:w-fit')}
            style={
              !isMobileDevice
                ? {
                    gap: `${windowInnerHeight - 150}px`,
                  }
                : undefined
            }
          >
            {mediaGroup?.map((item, index) => {
              return (
                <div
                  key={item.title}
                  data-id={item.title}
                  ref={(el) => {
                    if (el) {
                      elementsRef.current.set(index, el)
                    } else {
                      elementsRef.current.delete(index)
                    }
                  }}
                  className={clsx('flex justify-between items-start w-full h-fit', 'laptop:items-center')}
                >
                  <div className={clsx('w-full', 'laptop:w-100 h-fit')}>
                    {isMobileDevice && (
                      <div className={clsx('w-full h-250px overflow-hidden mb-6')}>
                        <PdMedia src={item.mediaUrl || ''} mask={false} />
                      </div>
                    )}
                    <div
                      className={clsx('text-2xl font-semibold px-6', 'laptop:text-44 laptop:px-0', 'text-fc-primary')}
                    >
                      {item.title}
                    </div>
                    <div
                      className={clsx(
                        'text-base font-normal mt-1 px-6',
                        'laptop:text-lg laptop:mt-6 laptop:px-0',
                        'text-fc-secondary'
                      )}
                    >
                      {item.desc}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {!isMobileDevice && (
            <aside
              className={clsx(
                'sticky overflow-hidden flex-shrink-0 flex-grow-0',
                'top-164px bottom-78px w-0 h-0 mb-6 rounded-20px',
                'laptop:top-[calc(50vh-11.625rem)]',
                'laptop:bottom-0 laptop:w-750px laptop:h-125 laptop:mb-0 laptop:rounded-40px',
                'bg-container-primary'
              )}
            >
              {mediaGroup?.map((item, index) => (
                <div
                  key={item.title}
                  className={clsx('absolute top-0 left-0 transition-opacity duration-700', 'w-full h-full', {
                    'opacity-100': activeId === item.title,
                    'opacity-0': activeId !== item.title,
                  })}
                >
                  <PdMedia src={item.mediaUrl || ''} mask={false} />
                </div>
              ))}
            </aside>
          )}
        </div>
      </div>
    </section>
  )
}

export default ScrollSwitchMedia
